// src/pages/ProjectSeekachu.jsx
import { useEffect, useState } from 'react';
import '../styles/ProjectPage.css';
import CaseStudyToc from '../components/CaseStudyToc';
import BackToTop from '../components/BackToTop';
import Lightbox from '../components/Lightbox';
import { projectsData } from '../data/projects';

const STYLE_GALLERY = [
    {
        src: '/images/seekachu/exemple-ideal.jpg',
        alt: "Sélection personnelle de cartes partageant un même style d'illustration montagneux et brumeux, malgré des couleurs et Pokémon différents"
    },
    {
        src: '/images/seekachu/style-avant-orange.png',
        alt: 'Résultat réel du mode ADN visuel pour la carte Hoothoot, des résultats dominés par la couleur orange'
    },
    {
        src: '/images/seekachu/stye-apres-orange.png',
        alt: 'Résultat réel du mode Style pour la même carte, résultats plus variés mais encore mélangés en tête de liste'
    },
    {
        src: '/images/seekachu/shaofouine.png',
        alt: 'Carte Shaofouine (Faille Paradoxe), 74,5 % de correspondance en mode Style'
    },
    {
        src: '/images/seekachu/incisache.png',
        alt: 'Carte Incisache, 74,0 % de correspondance en mode Style'
    },
    {
        src: '/images/seekachu/shaofouine2.png',
        alt: 'Carte Shaofouine (Flamme Blanche), 72,7 % de correspondance en mode Style'
    }
];

const TOC_SECTIONS = [
    {
        id: 'product',
        label: 'Product',
        children: [
            { id: 'le-point-de-depart', label: 'Le point de départ' },
            { id: 'mes-decisions', label: 'Mes décisions' },
            { id: 'ce-que-jen-retiens', label: "Ce que j'en retiens" }
        ]
    },
    {
        id: 'tech',
        label: 'Tech',
        children: [
            { id: 'modes-de-recherche', label: 'Modes de recherche' },
            { id: 'pipeline', label: 'Le pipeline' },
            { id: 'roadmap', label: 'Roadmap' }
        ]
    }
];

export default function ProjectSeekachu() {
    const project = projectsData.find((p) => p.id === 'seekachu');
    const [lightboxIndex, setLightboxIndex] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="project-page">
            <main className="project-main figma-project-main">
                <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="figma-embed-wrapper seekachu-preview-link"
                >
                    <picture>
                        <source
                            type="image/webp"
                            srcSet="/images/preview/preview-seekachu-mobile.webp 900w, /images/preview/preview-seekachu.webp 1800w"
                            sizes="(max-width: 932px) 300px, 1250px"
                        />
                        <img
                            src={project.imgUrl}
                            srcSet="/images/preview/preview-seekachu-mobile.jpg 900w, /images/preview/preview-seekachu.jpg 1800w"
                            sizes="(max-width: 932px) 300px, 1250px"
                            alt="Aperçu du site Seekachu"
                            className="seekachu-preview-img"
                            loading="lazy"
                            decoding="async"
                        />
                    </picture>
                    <span className="seekachu-preview-overlay">Voir le site en ligne ↗</span>
                </a>

                <div className="project-content">
                    <h1>{project.title}</h1>
                    <p className="project-desc">{project.desc}</p>

                    {project.tech?.length > 0 && (
                        <div className="project-tech">
                            <h3>Compétences</h3>
                            <ul>
                                {project.tech.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <p className="project-preview-hint">À explorer en cliquant</p>
                </div>
            </main>

            <CaseStudyToc sections={TOC_SECTIONS} />

            <section className="case-study case-study--inline">
                <section className="case-study-section">
                    <h2 id="product">Product</h2>

                    <h3 id="le-point-de-depart">Le point de départ</h3>
                    <p>
                        Les moteurs de recherche de cartes Pokémon fonctionnent tous par mots-clés :
                        nom, extension, rareté. Mais quand un collectionneur cherche une carte, il ne
                        cherche pas toujours un nom. Il cherche souvent un style : cette illustration en
                        aquarelle, cette dominante bleue, ce trait d'artiste. Le texte ne permet pas de
                        formuler ça. C'est ce décalage qui m'a donné envie de construire Seekachu.
                    </p>

                    <h3 id="mes-decisions">Mes décisions</h3>
                    <ul className="decision-list">
                        <li>
                            <strong>Chercher par image plutôt que par texte.</strong>
                            J'ai choisi de partir d'une carte de référence : tu en sélectionnes une, et
                            le moteur te renvoie celles qui lui ressemblent visuellement. Ça supprime le
                            problème de formuler une intention esthétique avec des mots. Ci-dessous, un
                            exemple de ce que « se ressembler visuellement » veut dire concrètement.

                            <figure className="decision-figure">
                                <button type="button" onClick={() => setLightboxIndex(0)}>
                                    <img src={STYLE_GALLERY[0].src} alt={STYLE_GALLERY[0].alt} loading="lazy" />
                                </button>
                                <figcaption>
                                    Ma propre sélection, pour illustrer ce que « même style » veut dire
                                    ici : une ambiance montagneuse et brumeuse, indépendamment de la
                                    couleur du Pokémon ou de l'extension.
                                </figcaption>
                            </figure>
                        </li>
                        <li>
                            <strong>Séparer les modes de recherche.</strong>
                            Ressembler, ça peut vouloir dire plusieurs choses. Même ambiance générale,
                            même style de trait, même palette, même artiste. J'ai découpé en quatre
                            modes explicites plutôt que de fusionner en un seul score de similarité,
                            parce qu'un résultat unique aurait mélangé des critères que l'utilisateur ne
                            pouvait ni comprendre ni ajuster.
                        </li>
                        <li>
                            <strong>Neutraliser la couleur pour le mode Style.</strong>
                            Sur mes premiers tests, la recherche par style renvoyait surtout des cartes
                            de la même couleur, jamais du même style : deux illustrations avec un même
                            fond de montagnes et de ciel auraient dû ressortir comme proches, mais je
                            n'obtenais que des cartes à dominante orange, sans lien de composition. Ainsi
                            j'ai fait le choix de catégoriser les critères plutôt que de les mélanger : un
                            mode pour la couleur, un pour le style, un pour la composition. La couleur
                            écrasait malgré tout le mode Style, donc j'ai ajouté des embeddings en niveaux
                            de gris pour isoler le trait et la composition, ce qui l'a rendu vraiment
                            distinct des autres au lieu d'être une variante de la palette.

                            <figure className="decision-figure">
                                <button type="button" onClick={() => setLightboxIndex(1)}>
                                    <img src={STYLE_GALLERY[1].src} alt={STYLE_GALLERY[1].alt} loading="lazy" />
                                </button>
                                <figcaption>
                                    Résultat réel du mode ADN visuel (couleur non neutralisée), capturé
                                    en phase de développement, pour cette même carte de référence : la
                                    teinte orange domine, au détriment du style recherché.
                                </figcaption>
                            </figure>

                            <figure className="decision-figure">
                                <button type="button" onClick={() => setLightboxIndex(2)}>
                                    <img src={STYLE_GALLERY[2].src} alt={STYLE_GALLERY[2].alt} loading="lazy" />
                                </button>
                                <figcaption>
                                    Résultat réel du mode Style, une fois la couleur neutralisée : les
                                    meilleurs scores restent mélangés (rochers, textures proches), mais
                                    les cartes montagneuses recherchées apparaissent en scrollant un peu
                                    plus loin dans la liste.
                                </figcaption>
                            </figure>

                            <figure className="decision-figure decision-figure-grid">
                                {[3, 4, 5].map((i) => (
                                    <button type="button" key={STYLE_GALLERY[i].src} onClick={() => setLightboxIndex(i)}>
                                        <img src={STYLE_GALLERY[i].src} alt={STYLE_GALLERY[i].alt} loading="lazy" />
                                    </button>
                                ))}
                                <figcaption>
                                    Trouvées plus loin dans la liste des résultats, entre 72 % et 74,5 %
                                    de correspondance.
                                </figcaption>
                            </figure>

                            Trois de ces cartes montagneuses, trouvées entre 72 % et 74,5 % de
                            correspondance : le style neutralisé les rapproche via la texture, le
                            contraste et la composition, pas via une compréhension sémantique de la
                            scène. C'est pour ça qu'une carte rocheuse ou texturée peut encore se classer
                            devant une vraie scène de montagne.
                        </li>
                        <li>
                            <strong>Assumer un chargement lent au premier accès.</strong>
                            Seekachu est un projet personnel, hébergé sur l'offre gratuite de Render :
                            pas de budget pour un serveur qui calculerait les similarités à la demande.
                            Le modèle tourne donc côté client. J'ai préféré un temps de chargement
                            initial visible plutôt qu'un coût serveur qui aurait rendu le projet non
                            viable pour un usage passion. J'ai compressé les données de 197 Mo à 73 Mo
                            pour que ce compromis reste acceptable.
                        </li>
                    </ul>

                    <h3 id="ce-que-jen-retiens">Ce que j'en retiens</h3>
                    <p>
                        Le mode Style est celui qui m'a le plus appris. La première version marchait
                        techniquement mais ne répondait pas au besoin : les résultats étaient corrects
                        au sens du modèle, inutiles au sens de l'utilisateur. C'est ce qui m'a fait
                        comprendre qu'un critère doit être compréhensible pour être utile, pas seulement
                        juste. Le projet reste en cours, cette neutralisation n'est pas encore parfaite,
                        mais Seekachu répond déjà à mon besoin principal : retrouver des cartes par
                        ressemblance visuelle sans avoir à la décrire avec des mots.
                    </p>
                </section>

                <section className="case-study-section">
                    <h2 id="tech">Tech</h2>
                    <p>
                        Le frontend se contente d'afficher une intention de recherche. Tout le travail
                        se passe ailleurs : extraction visuelle par CLIP, calcul de similarité côté
                        serveur, résultats renvoyés triés.
                    </p>

                    <div className="stack-grid">
                        <div className="stack-item">
                            <span className="stack-label">Frontend</span>
                            <span className="stack-value">React 19 + TypeScript (Vite 7)</span>
                        </div>
                        <div className="stack-item">
                            <span className="stack-label">Backend</span>
                            <span className="stack-value">Node.js + Express 5 + TypeScript</span>
                        </div>
                        <div className="stack-item">
                            <span className="stack-label">IA / Vision</span>
                            <span className="stack-value">CLIP via Transformers.js (local)</span>
                        </div>
                        <div className="stack-item">
                            <span className="stack-label">Recherche vectorielle</span>
                            <span className="stack-value">Similarité cosinus côté serveur</span>
                        </div>
                        <div className="stack-item">
                            <span className="stack-label">Recherche textuelle</span>
                            <span className="stack-value">Fuse.js (fuzzy search multi-token)</span>
                        </div>
                        <div className="stack-item">
                            <span className="stack-label">Données</span>
                            <span className="stack-value">PokemonTCG/pokemon-tcg-data + TCGdex</span>
                        </div>
                    </div>

                    <h3 id="modes-de-recherche">Les quatre modes de recherche</h3>
                    <div className="mode-grid">
                        <div className="mode-card">
                            <span className="mode-icon">🧬</span>
                            <h4>ADN Visuel</h4>
                            <p>Similarité cosinus sur embeddings CLIP 768D</p>
                        </div>
                        <div className="mode-card">
                            <span className="mode-icon">🎭</span>
                            <h4>Style</h4>
                            <p>Même calcul, sur embeddings en niveaux de gris (trait, composition)</p>
                        </div>
                        <div className="mode-card">
                            <span className="mode-icon">🎨</span>
                            <h4>Palette</h4>
                            <p>Comparaison HSL des 3 couleurs dominantes</p>
                        </div>
                        <div className="mode-card">
                            <span className="mode-icon">✏️</span>
                            <h4>Illustrateur</h4>
                            <p>Lookup direct par nom d'artiste</p>
                        </div>
                    </div>

                    <h3 id="pipeline">Le pipeline</h3>
                    <ol className="pipeline-list">
                        <li>
                            <strong>Collecte.</strong>
                            Pokécardex, la référence française, n'a pas d'API publique exploitable à
                            grande échelle. J'ai construit la base sur pokemontcg.io (complet, stable,
                            mais en anglais), complété par TCGdex pour les données et traductions
                            françaises. Certaines extensions récentes ont peu de métadonnées
                            d'illustrateur côté API : un script recense ces trous pour prioriser les
                            corrections.
                        </li>
                        <li>
                            <strong>Extraction.</strong>
                            Chaque image passe dans CLIP (Transformers.js), qui en sort un vecteur de
                            768 dimensions.
                        </li>
                        <li>
                            <strong>Double encodage.</strong>
                            Une version couleur pour l'ADN visuel, une version niveaux de gris pour
                            isoler le style.
                        </li>
                        <li>
                            <strong>Tagging sémantique.</strong>
                            Chaque carte est aussi comparée à douze prompts descriptifs (portrait,
                            paysage, scène de combat, aquarelle, rendu 3D, croquis...) pour obtenir un
                            score de correspondance par catégorie. Le script est reprenable, sauvegarde
                            sa progression toutes les 50 cartes, et tourne plusieurs heures sur
                            l'ensemble de la base.
                        </li>
                        <li>
                            <strong>Format binaire.</strong>
                            Les vecteurs sont stockés en <code>.bin</code> pour réduire l'empreinte
                            mémoire.
                        </li>
                        <li>
                            <strong>Recherche.</strong>
                            À la sélection d'une carte, le serveur calcule la similarité cosinus avec
                            les ~20 000 vecteurs et renvoie les plus proches.
                        </li>
                    </ol>

                    <h3 id="roadmap">Roadmap</h3>
                    <ul className="roadmap-list">
                        <li className="roadmap-item roadmap-item--done">
                            <input type="checkbox" checked disabled aria-hidden="true" />
                            <span>
                                <strong>Extension à toutes les séries.</strong>
                            </span>
                        </li>
                        <li className="roadmap-item roadmap-item--done">
                            <input type="checkbox" checked disabled aria-hidden="true" />
                            <span>
                                <strong>Quatre modes de recherche.</strong>
                            </span>
                        </li>
                        <li className="roadmap-item">
                            <input type="checkbox" disabled aria-hidden="true" />
                            <span>
                                <strong>Portfolio personnel.</strong> Marquer les cartes qu'on possède
                                pour suivre sa collection, ce qui nécessite d'abord une vraie base de
                                données (comptes, état persistant).
                            </span>
                        </li>
                        <li className="roadmap-item">
                            <input type="checkbox" disabled aria-hidden="true" />
                            <span>
                                <strong>Migration vers pgvector.</strong> Une fois la base en place,
                                unifier le stockage des embeddings : un bonus de simplicité, pas un
                                besoin de performance à l'échelle actuelle.
                            </span>
                        </li>
                        <li className="roadmap-item roadmap-item--done">
                            <input type="checkbox" checked disabled aria-hidden="true" />
                            <span>
                                <strong>Interface bilingue FR / EN.</strong>
                            </span>
                        </li>
                        <li className="roadmap-item">
                            <input type="checkbox" disabled aria-hidden="true" />
                            <span>
                                <strong>Support multilingue JP / CN.</strong>
                            </span>
                        </li>
                    </ul>

                    <p className="case-study-note case-study-note--caption">
                        Le code a été écrit avec l'assistance d'outils IA pour l'architecture et le
                        prototypage.
                    </p>
                </section>
            </section>

            <Lightbox
                images={STYLE_GALLERY}
                index={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                onNavigate={setLightboxIndex}
            />

            <BackToTop />
        </div>
    );
}
